import { ROUTES } from "@shared/constants/routes";
import { workoutExecutionStore } from "@features/workout/execution/workoutExecutionStore";

export type WorkoutCompletionSnapshot = {
  calories: number;
  completedExercises: number;
  exercisesAmount: number;
  totalSeconds: number;
  totalMinutes: number;
};

const getSnapshotKey = (trainingID: number) =>
  `workoutCompletionSnapshot:${trainingID}`;
const WORKOUT_EXECUTION_PROGRESS_KEY = "workoutExecutionProgress";
const getLegacyWorkoutElapsedKey = (trainingID: number) =>
  `workoutExecutionElapsedSeconds:${trainingID}`;
const getLegacyWorkoutCompletedExercisesKey = (trainingID: number) =>
  `workoutExecutionCompletedExercises:${trainingID}`;

const getElapsedSeconds = (startedAt: number, completedAt: number) =>
  Math.max(0, Math.round((completedAt - startedAt) / 1000));

type WorkoutExecutionProgress = {
  completedExercises: number;
  elapsedSeconds: number;
};

type WorkoutExecutionProgressStorage = Record<string, WorkoutExecutionProgress>;

export const getApproxWorkoutCalories = (
  minutes: number,
  weightKg?: number | null,
) => {
  const safeWeightKg =
    typeof weightKg === "number" && Number.isFinite(weightKg) && weightKg > 0
      ? weightKg
      : 75;
  const met = 6;
  return Math.max(1, Math.round((met * 3.5 * safeWeightKg * minutes) / 200));
};

const normalizePositiveNumber = (value: unknown, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : fallback;

const normalizeNonNegativeNumber = (value: unknown, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : fallback;

const normalizeProgress = (
  value: Partial<WorkoutExecutionProgress> | undefined,
): WorkoutExecutionProgress => ({
  completedExercises: normalizeNonNegativeNumber(value?.completedExercises, 0),
  elapsedSeconds: normalizeNonNegativeNumber(value?.elapsedSeconds, 0),
});

const readProgressStorage = (): WorkoutExecutionProgressStorage => {
  try {
    const rawValue = window.localStorage.getItem(WORKOUT_EXECUTION_PROGRESS_KEY);
    if (!rawValue) return {};

    const parsedValue = JSON.parse(rawValue) as Record<
      string,
      Partial<WorkoutExecutionProgress> | undefined
    >;

    return Object.fromEntries(
      Object.entries(parsedValue)
        .filter(([, value]) => value && typeof value === "object")
        .map(([trainingID, value]) => [trainingID, normalizeProgress(value)]),
    );
  } catch {
    return {};
  }
};

const writeProgressStorage = (storage: WorkoutExecutionProgressStorage) => {
  window.localStorage.setItem(
    WORKOUT_EXECUTION_PROGRESS_KEY,
    JSON.stringify(storage),
  );
};

const readLegacyProgress = (trainingID: number): WorkoutExecutionProgress => {
  try {
    const elapsedSeconds = Number(
      window.localStorage.getItem(getLegacyWorkoutElapsedKey(trainingID)),
    );
    const completedExercises = Number(
      window.localStorage.getItem(
        getLegacyWorkoutCompletedExercisesKey(trainingID),
      ),
    );

    return {
      completedExercises:
        Number.isFinite(completedExercises) && completedExercises > 0
          ? completedExercises
          : 0,
      elapsedSeconds:
        Number.isFinite(elapsedSeconds) && elapsedSeconds > 0
          ? elapsedSeconds
          : 0,
    };
  } catch {
    return {
      completedExercises: 0,
      elapsedSeconds: 0,
    };
  }
};

const removeLegacyProgress = (trainingID: number) => {
  window.localStorage.removeItem(getLegacyWorkoutElapsedKey(trainingID));
  window.localStorage.removeItem(getLegacyWorkoutCompletedExercisesKey(trainingID));
};

const readWorkoutExecutionProgress = (
  trainingID: number,
): WorkoutExecutionProgress => {
  const storage = readProgressStorage();
  const storedProgress = storage[String(trainingID)];

  if (storedProgress) {
    return storedProgress;
  }

  const legacyProgress = readLegacyProgress(trainingID);
  if (
    legacyProgress.elapsedSeconds > 0 ||
    legacyProgress.completedExercises > 0
  ) {
    try {
      writeProgressStorage({
        ...storage,
        [trainingID]: legacyProgress,
      });
      removeLegacyProgress(trainingID);
    } catch {
      return legacyProgress;
    }
  }

  return legacyProgress;
};

const writeWorkoutExecutionProgress = (
  trainingID: number,
  progress: WorkoutExecutionProgress,
) => {
  const storage = readProgressStorage();

  writeProgressStorage({
    ...storage,
    [trainingID]: normalizeProgress(progress),
  });
  removeLegacyProgress(trainingID);
};

const removeWorkoutExecutionProgress = (trainingID: number) => {
  const storage = readProgressStorage();
  delete storage[String(trainingID)];

  writeProgressStorage(storage);
  removeLegacyProgress(trainingID);
};

export const buildWorkoutCompletionPath = (trainingID: number) =>
  ROUTES.training.completed.replace(":trainingID", String(trainingID));

export const resetWorkoutExecutionElapsed = (trainingID: number) => {
  try {
    writeWorkoutExecutionProgress(trainingID, {
      completedExercises: 0,
      elapsedSeconds: 0,
    });
  } catch {
    return;
  }
};

export const readWorkoutExecutionElapsedSeconds = (trainingID: number) => {
  return readWorkoutExecutionProgress(trainingID).elapsedSeconds;
};

export const readWorkoutExecutionCompletedExercises = (trainingID: number) => {
  return readWorkoutExecutionProgress(trainingID).completedExercises;
};

export const addWorkoutExecutionResult = ({
  durationSeconds,
  isCompleted,
  trainingID,
}: {
  durationSeconds: number;
  isCompleted: boolean;
  trainingID: number;
}) => {
  const nextElapsedSeconds =
    readWorkoutExecutionProgress(trainingID).elapsedSeconds +
    Math.max(0, Math.round(durationSeconds));
  const nextCompletedExercises =
    readWorkoutExecutionProgress(trainingID).completedExercises +
    (isCompleted ? 1 : 0);

  try {
    writeWorkoutExecutionProgress(trainingID, {
      completedExercises: nextCompletedExercises,
      elapsedSeconds: nextElapsedSeconds,
    });
  } catch {
    return {
      completedExercises: nextCompletedExercises,
      totalSeconds: nextElapsedSeconds,
    };
  }

  return {
    completedExercises: nextCompletedExercises,
    totalSeconds: nextElapsedSeconds,
  };
};

export const getWorkoutWallClockDuration = ({
  completedAt,
  startedAt,
}: {
  completedAt: number;
  startedAt: number;
}) => {
  return getElapsedSeconds(startedAt, completedAt);
};

export const saveWorkoutCompletionSnapshot = ({
  exerciseDurationSeconds,
  exercisesAmount,
  isExerciseCompleted,
  trainingID,
  userWeightKg,
}: {
  exerciseDurationSeconds: number;
  exercisesAmount: number;
  isExerciseCompleted: boolean;
  trainingID: number;
  userWeightKg?: number | null;
}) => {
  const safeExercisesAmount = Math.max(exercisesAmount || 1, 1);
  const progress = addWorkoutExecutionResult({
    durationSeconds: exerciseDurationSeconds,
    isCompleted: isExerciseCompleted,
    trainingID,
  });
  const totalSeconds = progress.totalSeconds;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const snapshot: WorkoutCompletionSnapshot = {
    calories: getApproxWorkoutCalories(Math.max(totalSeconds / 60, 1 / 60), userWeightKg),
    completedExercises: Math.min(progress.completedExercises, safeExercisesAmount),
    exercisesAmount: safeExercisesAmount,
    totalSeconds,
    totalMinutes,
  };

  try {
    window.sessionStorage.setItem(
      getSnapshotKey(trainingID),
      JSON.stringify(snapshot),
    );
    removeWorkoutExecutionProgress(trainingID);
  } catch {
    return snapshot;
  }

  return snapshot;
};

export const readWorkoutCompletionSnapshot = (
  trainingID: number,
): WorkoutCompletionSnapshot | null => {
  try {
    const rawValue = window.sessionStorage.getItem(getSnapshotKey(trainingID));
    if (!rawValue) return null;

    const parsedValue = JSON.parse(
      rawValue,
    ) as Partial<WorkoutCompletionSnapshot>;
    const fallbackExercisesAmount =
      workoutExecutionStore.exercisesAmount || parsedValue.exercisesAmount || 1;

    return {
      calories: normalizePositiveNumber(parsedValue.calories, 1),
      completedExercises: normalizeNonNegativeNumber(
        parsedValue.completedExercises,
        0,
      ),
      exercisesAmount: normalizePositiveNumber(
        parsedValue.exercisesAmount,
        fallbackExercisesAmount,
      ),
      totalSeconds: normalizeNonNegativeNumber(
        parsedValue.totalSeconds,
        normalizeNonNegativeNumber(parsedValue.totalMinutes, 0) * 60,
      ),
      totalMinutes: normalizeNonNegativeNumber(parsedValue.totalMinutes, 0),
    };
  } catch {
    return null;
  }
};
